import { Close, Info, Warning } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { JSONPath } from 'jsonpath-plus';
import React from 'react';
import EditText from './EditText/EditText.js';

/**
 * A self-contained editable field component with warning display and dismissal functionality.
 * Uses JSONPath to extract values and warnings from caseInfo, and handles its own save/clear operations.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.caseInfo - The case data object
 * @param {Function} props.setCaseInfo - Function to update caseInfo in parent component
 * @param {string} props.textPath - JSONPath to the field value (e.g., "$.documents['doc1'].analysis.medical_chronology[0].patient_name")
 * @param {string} [props.needsReviewPath] - Optional JSONPath to the needs_review warning
 * @param {string} [props.caveatsPath] - Optional JSONPath to the caveats warning
 * @param {Function} props.formatValue - Optional function to format the display value (default: identity function)
 * @param {Function} props.parseValue - Optional function to parse the input value back to the original type (default: identity function)
 * @param {Object} props.callbackArguments - Optional arguments to pass to formatValue function (e.g., { currency: 'USD', locale: 'en-US' })
 * @param {boolean} props.isEventDetail - Whether this is an event detail field (affects styling)
 * @param {Function} props.setSnackBarMessage - Function to show user feedback messages
 * @param {Object} props.style - Optional custom styling for the EditText component
 * @param {string} props.placeholder - Optional placeholder text
 */
const EditableFieldWithWarnings = ({
  caseInfo,
  setCaseInfo,
  textPath,
  needsReviewPath = null,
  caveatsPath = null,
  formatValue = (value) => value || '',
  parseValue = (value) => value,
  callbackArguments = {},
  onWarningClick = () => { },
  isEventDetail = false,
  setSnackBarMessage,
  style = {},
  placeholder,
  readonly = false
}) => {
  // Extract value from caseInfo using jsonpath-plus
  const getValueFromCaseInfo = (path, defaultValue = null) => {
    if (!path) {
      return defaultValue;
    }
    try {
      const results = JSONPath({
        json: caseInfo,
        path: path,
        resultType: 'value'
      });
      // if (results.length === 0) {
      //   console.log('No results found for path:', path);
      // }
      return results.length > 0 ? results[0] : defaultValue;
    } catch (error) {
      console.error('[FATAL] Error extracting value from path:', path, error);
      return defaultValue;
    }
  };

  // Update caseInfo using JSONPath to set a value at the specified path
  const updateCaseInfoValue = (path, value) => {
    try {
      // Deep clone caseInfo to avoid mutating the original
      const updatedCaseInfo = JSON.parse(JSON.stringify(caseInfo));

      // Use JSONPath to find and update the value
      JSONPath({
        json: updatedCaseInfo,
        path: path,
        resultType: 'all',
        callback: (initialValue, resultType, fullPayload) => {
          const { parent, parentProperty } = fullPayload;
          parent[parentProperty] = value === null ? undefined : value;
        },
      });

      console.log('Updating caseInfo with updatedCaseInfo: ' + JSON.stringify(updatedCaseInfo));
      setCaseInfo(updatedCaseInfo);
    } catch (error) {
      console.error('Error updating caseInfo value at path:', path, error);
    }
  };

  // Save field value to server
  const handleSave = async (data) => {
    // Check if there was a parsing error
    if (data.error) {
      setSnackBarMessage('Error: ' + data.error);
      // EditText will handle reverting to the previous formatted value internally
      return;
    }

    // Extract the parsed value from the EditText data object
    const value = data.value;
    const formattedValue = data.formattedValue !== undefined ? data.formattedValue : value;
    const valueInUserMessages = '"' + (String(formattedValue).length > 23 ? String(formattedValue).substring(0, 20) + '...' : String(formattedValue)) + '"';

    try {
      const response = await fetch(`/api/case/${caseInfo.caseId}/field`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [textPath]: value }),
      });

      if (!response.ok) {
        console.error('Error persisting field to server:', response.statusText);
        setSnackBarMessage('Error saving: ' + valueInUserMessages);
        return;
      }

      const result = await response.json();
      console.log('Successfully persisted field to server:', result);
      setSnackBarMessage('Updated value to ' + valueInUserMessages);

      // Update caseInfo immediately with the new value
      updateCaseInfoValue(textPath, value);
    } catch (error) {
      console.error('Error persisting field to server:', error);
      setSnackBarMessage('Error saving: ' + valueInUserMessages);
    }
  };

  // Clear warning from server
  const handleClearWarning = async (warningType) => {
    const warningPath = warningType === 'needs_review' ? needsReviewPath : caveatsPath;
    const userVisibleWarningType = warningType === 'needs_review' ? 'review item' : 'note';

    try {
      const response = await fetch(`/api/case/${caseInfo.caseId}/field`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [warningPath]: null }),
      });

      if (!response.ok) {
        console.error('Error clearing warning:', response.statusText);
        setSnackBarMessage('Error clearing ' + userVisibleWarningType);
        return;
      }

      // Update caseInfo to remove the warning
      updateCaseInfoValue(warningPath, null);

      setSnackBarMessage(`Successfully cleared ${userVisibleWarningType}`);
    } catch (error) {
      console.error('Error clearing warning:', error);
      setSnackBarMessage('Error clearing ' + userVisibleWarningType);
    }
  };

  const currentValue = getValueFromCaseInfo(textPath, '');
  const needsReview = getValueFromCaseInfo(needsReviewPath);
  const caveats = getValueFromCaseInfo(caveatsPath);

  // Render warning messages
  const renderWarningMessages = () => {
    const warningMessages = [];

    if (needsReview) {
      const warningText = needsReview;
      warningMessages.push(
        <Box
          onClick={onWarningClick}
          key="needs-review"
          className="needs-review"
          sx={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 0.5,
            padding: isEventDetail ? '3px 5px' : '4px 6px',
            borderRadius: isEventDetail ? '3px' : '4px',
            backgroundColor: 'rgba(244, 67, 54, 0.05)',
            border: `1px solid rgba(244, 67, 54, ${isEventDetail ? '0.15' : '0.2'})`,
            position: 'relative',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(244, 67, 54, 0.08)',
              transform: 'translateY(-1px)',
              boxShadow: 'var(--shadow-subtle)',
              '& .clear-button': {
                opacity: 1,
                visibility: 'visible'
              }
            }
          }}
        >
          <Warning sx={{
            color: 'var(--error-color)',
            fontSize: isEventDetail ? '11px' : '14px',
            flexShrink: 0
          }} />
          <Typography sx={{
            color: 'var(--error-color)',
            fontSize: isEventDetail ? '10px' : '12px',
            fontWeight: 500,
            flex: 1,
            lineHeight: isEventDetail ? 1.2 : 1.3
          }}>
            {warningText}
          </Typography>
          <IconButton
            className="clear-button"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleClearWarning('needs_review');
            }}
            sx={{
              color: 'var(--error-color)',
              padding: isEventDetail ? '1px' : '2px',
              opacity: 0,
              visibility: 'hidden',
              transition: 'all 0.2s ease',
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              borderRadius: '50%',
              width: isEventDetail ? '16px' : '20px',
              height: isEventDetail ? '16px' : '20px',
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.2)',
                transform: 'translateY(-1px)',
                boxShadow: 'var(--shadow-subtle)',
              }
            }}
            aria-label="Clear warning"
          >
            <Close sx={{ fontSize: isEventDetail ? '10px' : '12px' }} />
          </IconButton>
        </Box>
      );
    }

    if (caveats) {
      const caveatText = caveats;
      warningMessages.push(
        <Box
          onClick={onWarningClick}
          key="caveats"
          className="caveat"
          sx={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 0.5,
            padding: isEventDetail ? '3px 5px' : '4px 6px',
            borderRadius: isEventDetail ? '3px' : '4px',
            backgroundColor: 'rgba(33, 150, 243, 0.05)',
            border: `1px solid rgba(33, 150, 243, ${isEventDetail ? '0.15' : '0.2'})`,
            position: 'relative',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(33, 150, 243, 0.08)',
              transform: 'translateY(-1px)',
              boxShadow: 'var(--shadow-subtle)',
              '& .clear-button': {
                opacity: 1,
                visibility: 'visible'
              }
            }
          }}
        >
          <Info sx={{
            color: 'var(--info-color)',
            fontSize: isEventDetail ? '11px' : '14px',
            flexShrink: 0
          }} />
          <Typography sx={{
            color: 'var(--info-color)',
            fontSize: isEventDetail ? '10px' : '12px',
            fontWeight: 500,
            flex: 1,
            lineHeight: isEventDetail ? 1.2 : 1.3
          }}>
            {caveatText}
          </Typography>
          <IconButton
            className="clear-button"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleClearWarning('caveats');
            }}
            sx={{
              color: 'var(--info-color)',
              padding: isEventDetail ? '1px' : '2px',
              opacity: 0,
              visibility: 'hidden',
              transition: 'all 0.2s ease',
              backgroundColor: 'rgba(33, 150, 243, 0.1)',
              borderRadius: '50%',
              width: isEventDetail ? '16px' : '20px',
              height: isEventDetail ? '16px' : '20px',
              '&:hover': {
                backgroundColor: 'rgba(33, 150, 243, 0.2)',
                transform: 'translateY(-1px)',
                boxShadow: 'var(--shadow-subtle)',
              }
            }}
            aria-label="Clear warning"
          >
            <Close sx={{ fontSize: isEventDetail ? '10px' : '12px' }} />
          </IconButton>
        </Box>
      );
    }

    return warningMessages;
  };

  const warningMessages = renderWarningMessages();
  // Pass the raw value to EditText, which will handle formatting internally
  const displayValue = currentValue;

  return (
    <Box>
      {warningMessages}
      <Box data-edittext className="edittext-container" onClick={(e) => e.stopPropagation()}>
        <EditText
          defaultValue={displayValue}
          placeholder={placeholder}
          readonly={readonly}
          type="textarea"
          onSave={handleSave}
          formatValue={formatValue}
          parseValue={parseValue}
          callbackArguments={callbackArguments}
          style={{
            color: 'var(--text-primary)',
            fontSize: isEventDetail ? '12px' : '14px',
            fontWeight: 'normal',
            minHeight: isEventDetail ? '18px' : '20px',
            padding: '2px 4px',
            borderRadius: '4px',
            border: 'none',
            background: 'transparent',
            cursor: "url(/edit.png) -32 32, pointer",
            width: '100%',
            display: isEventDetail ? 'inline' : 'block',
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'unset',
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            ...style
          }}
          inputClassName="editable-field-input"
        />
      </Box>
    </Box>
  );
};

export default EditableFieldWithWarnings;
